import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useRouter } from 'next/router'

import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

/**
 * Full-bleed wipe between routes.
 *
 * Panels sweep up to cover the outgoing page, then peel away from the
 * incoming one on a reverse stagger, so the two halves of the transition
 * read as one continuous movement rather than two separate animations.
 *
 * Only ever ONE timeline is alive. A prefetched route can fire
 * routeChangeStart and routeChangeComplete inside the same frame, and two
 * overlapping timelines then finish out of order, which is how anything the
 * cover half faded in used to get stranded on screen permanently.
 */
export const PageTransition: FC = (): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const timeline = useRef<gsap.core.Timeline | null>(null)

    const router = useRouter()

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }

        const panels = element.querySelectorAll<HTMLElement>('[data-panel]')

        const swap = (next: gsap.core.Timeline) => {
            timeline.current?.kill()
            timeline.current = next
        }

        const cover = () => {
            element.style.pointerEvents = 'auto'

            swap(
                gsap
                    .timeline()
                    .set(panels, { transformOrigin: 'bottom center' })
                    .to(panels, {
                        scaleY: 1,
                        duration: 0.62,
                        ease: 'power4.inOut',
                        stagger: 0.055
                    })
            )
        }

        const uncover = () => {
            swap(
                gsap
                    .timeline({
                        onComplete: () => {
                            element.style.pointerEvents = 'none'
                        }
                    })
                    .set(panels, { transformOrigin: 'top center' })
                    .to(panels, {
                        scaleY: 0,
                        duration: 0.68,
                        ease: 'power4.inOut',
                        stagger: { each: 0.055, from: 'end' }
                    })
            )
        }

        router.events.on('routeChangeStart', cover)
        router.events.on('routeChangeComplete', uncover)
        router.events.on('routeChangeError', uncover)

        return () => {
            router.events.off('routeChangeStart', cover)
            router.events.off('routeChangeComplete', uncover)
            router.events.off('routeChangeError', uncover)

            timeline.current?.kill()
            timeline.current = null
        }
    }, [router.events])

    return (
        <div
            ref={root}
            aria-hidden='true'
            className='pointer-events-none fixed inset-0 z-[90] flex'
        >
            {[0, 1, 2, 3, 4].map((panel) => (
                <div
                    key={panel}
                    data-panel
                    className='h-full flex-1 scale-y-0 bg-foreground will-change-transform'
                />
            ))}
        </div>
    )
}
